import React, { useState, useRef, useEffect, useCallback } from 'react';
import { GoogleMap, useLoadScript } from '@react-google-maps/api';
import { GOOGLE_MAPS_API, UW_TACOMA_GEOLOCATION } from '../../environment_variable';
import { COMPLETE_STREET_OBJECTS_ENDPOINT } from '../../configurations';
import axios from 'axios';
import { Box, CircularProgress } from '@mui/material';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend as LegendJS } from 'chart.js';
import Legend from './asset/Legend';
import CategoryCard from './asset/CategoryCard';
import Markers from './asset/Markers';
import { object_category, generateColorVariations, adjustColorBrightness } from './asset/Utilities';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, LegendJS);

const libraries = ['places'];

const mapContainerStyle = {
  width: 'calc(100vw - 400px)',
  height: '89vh',
  position: 'relative',
};

const center = {
  lat: UW_TACOMA_GEOLOCATION.Latitude,
  lng: UW_TACOMA_GEOLOCATION.Longitude,
};

const AssetManagement = () => {
  const [map, setMap] = useState(null);
  const [isLoading, setIsLoading] = useState(false); // Set initial loading state to true
  const [showDialog, setShowDialog] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [data, setData] = useState(object_category);

  const mapRef = useRef();

  useEffect(() => {
    setIsLoading(true);
    axios.get(COMPLETE_STREET_OBJECTS_ENDPOINT)
      .then(response => {
        const apiObjects = response.data;
        const updatedData = JSON.parse(JSON.stringify(object_category));

        apiObjects.forEach(obj => {
          const objectCategory = obj.category?.trim().toLowerCase();
          
          for (const [, mainConfig] of Object.entries(updatedData)) {
            const matchedSub = mainConfig.items.find(
              (subItem) => subItem.name.toLowerCase() === objectCategory
            );

            if (matchedSub) {
              matchedSub.items.push(obj);
              break;
            }
          }
        });
        
        setData(updatedData);

        // Adjust map bounds to fit all markers
        if (mapRef.current) {
          const bounds = new window.google.maps.LatLngBounds();
          Object.keys(updatedData).forEach((category) => {
            const categoryData = updatedData[category];
            if (categoryData && Array.isArray(categoryData.items)) {
              categoryData.items.forEach((item) => {
                if (Array.isArray(item.items)) {
                  item.items.forEach((nestedItem) => {
                    if (nestedItem.rough_location) {
                      bounds.extend({
                        lat: nestedItem.rough_location.lat,
                        lng: nestedItem.rough_location.lng,
                      });
                    }
                  });
                } else if (item.rough_location) {
                  bounds.extend({
                    lat: item.rough_location.lat,
                    lng: item.rough_location.lng,
                  });
                }
              });
            }
          });
          mapRef.current.fitBounds(bounds);
        }
      })
      .catch(error => {
        console.error("Error fetching objects:", error);
      })
      .finally(() => {
        setIsLoading(false); // Stop loading once data is fetched
      });
  }, []);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API,
    libraries,
  });

  const handleExpandClick = useCallback((index) => {
    setExpanded((prevExpanded) => (prevExpanded === index ? null : index));
  }, []);

  const chartData = useCallback((category) => {
    const categoryData = data[category];
    const barColors = generateColorVariations(categoryData.color, categoryData.items.length);
  
    return {
      labels: categoryData.items.map((item) => item.name),
      datasets: [
        {
          label: 'Number of Assets',
          data: categoryData.items.map((item) => Array.isArray(item.items) ? item.items.length : 0),
          backgroundColor: barColors,
          borderColor: barColors.map((color) => adjustColorBrightness(color, -10)),
          borderWidth: 1,
        },
      ],
    };
  }, [data]);

  if (loadError) return <div>Error loading maps</div>;
  if (!isLoaded) return <div>Loading maps</div>;
    
  return (
    <div style={{ position: 'relative' }}>
      {isLoading && ( // Show the spinner while loading
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 2000,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#fff', // White background for the box
            padding: '20px', // Add padding for spacing
            borderRadius: '12px', // Rounded corners
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)', // Subtle shadow for a modern look
          }}
        >
          <CircularProgress size={60} thickness={4} />
          <p style={{
            marginTop: '10px',
            fontSize: '16px',
            fontWeight: '500',
            color: '#555',
          }}>
            Please wait...
          </p>
        </Box>
      )}

      <Legend data={data} />

      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        zoom={14}
        center={center}
        onLoad={(mapInstance) => {
          setMap(mapInstance);
          mapRef.current = mapInstance;
        }}
        ref={mapRef}
        mapTypeId="satellite"
      >
        <Markers data={data} />
      </GoogleMap>

      {showDialog && (
        <div className="absolute top-0 right-0 bg-white p-2 rounded-lg w-96 h-full overflow-y-auto">
          <div className="mt-6">
            <div style={{padding: 10}}/>

            <Box sx={{ padding: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {data && Object.keys(data).map((category, index) => {
                const categoryData = data[category];
                if (!categoryData || !Array.isArray(categoryData.items)) return null;

                return (
                  <CategoryCard
                    key={index}
                    category={category}
                    categoryData={categoryData}
                    expanded={expanded}
                    handleExpandClick={handleExpandClick}
                    chartData={chartData}
                    index={index}
                  />
                );
              })}
            </Box>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetManagement;